import { useIntl } from "react-intl";
import FileSaver from 'file-saver';
import { BulkTest, BulkTestPageParam, Page } from "@/model";
import { UTCToLocalString } from '@/util/dateUtil';
import bulkTestRepository from "@/repository/bulkTestRepository";
import { useScenario } from "@/query/scenario";
import { useScenarioId } from "@/hook";
import Tooltip from "@/component/Tooltip";
import HighlightText from "@/component/HighlightText";
import SortButton from "@/component/SortButton";
import { DownloadIcon } from "@/assets/svgs/icons";
import { ReactComponent as LoadingIcon } from "@/assets/svgs/loading.svg";


interface BulkTestTableProps {
    pageData: Page<BulkTest>;
    pageParam: BulkTestPageParam;
    testingId: string | undefined;
    testingProgress: number;
    onClickSortHead: (key: BulkTestPageParam["sortKey"]) => void;
}

const BulkTestTable: React.FC<BulkTestTableProps> = (props) => {
    const { pageData: bulkTestPage, pageParam, testingId, testingProgress, onClickSortHead } = props;

    const intl = useIntl();
    const scenarioId = useScenarioId();

    const { data: scenario } = useScenario(scenarioId);

    const ariaSort = (key: BulkTestPageParam["sortKey"]): "ascending" | "descending" | "none" => {
        if (pageParam.sortKey !== key) {
            return "none";
        }
        return pageParam.sort === "asc" ? "ascending" : "descending";
    };

    const defaultTestCaseFileName = `${scenario?.name}_test_case.xlsx`;
    
    const onClickDownloadTestCase = async (bulkTest: BulkTest) => {
        const blob = await bulkTestRepository.getScenarioTestCase(scenarioId, bulkTest.id);
        FileSaver.saveAs(
            new Blob([blob], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            }),
            bulkTest.file_name ?? defaultTestCaseFileName
        );
    };
    
    const onClickDownloadTestResult = async (bulkTest: BulkTest) => {
        const blob = await bulkTestRepository.getScenarioTestResult(scenarioId, bulkTest.id);
        FileSaver.saveAs(
            new Blob([blob], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            }),
            `${scenario?.name}_test_result.xlsx`
        );
    };

    return (
        <div role="table"
             aria-label={intl.formatMessage({ id: "m04" })}
             className="selection-table"
        >
            <div role="rowgroup"
                 className="selection-table-thead grid-table-bulk-test"
            >
                <div role="columnheader"
                     className="selection-table-td px-[25px] text-black-500 font-[600]"
                >
                    {intl.formatMessage({ id: "i05002" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[15px]"
                >
                    {intl.formatMessage({ id: "i05003" })}
                </div>
                <div role="columnheader"
                     className="selection-table-th pl-[15px]"
                >
                    {intl.formatMessage({ id: "i05004" })}
                </div>
                <div role="columnheader"
                     aria-sort={ariaSort("status")}
                     className="selection-table-th pl-[15px]"
                >
                    <SortButton order={pageParam.sort}
                                active={pageParam.sortKey === "status"}
                                onClick={() => onClickSortHead("status")}
                    >
                        {intl.formatMessage({ id: "i05006" })}
                    </SortButton>
                </div>
                <div role="columnheader"
                     aria-sort={ariaSort("created_at")}
                     className="selection-table-th pl-[15px]"
                >
                    <SortButton order={pageParam.sort}
                                active={pageParam.sortKey === "created_at"}
                                onClick={() => onClickSortHead("created_at")}
                    >
                        {intl.formatMessage({ id: "i05005" })}
                    </SortButton>
                </div>
            </div>
            <div role="rowgroup"
                 className="selection-table-tbody"
            >
                {bulkTestPage.content.map(bulkTest => (
                    <div key={bulkTest.id}
                         role="row"
                         className="selection-table-tr group grid-table-bulk-test"
                    >
                        <div role="cell"
                             className="selection-table-td px-[25px]"
                        >
                            <HighlightText query={bulkTest.title ?? "-"}
                                           keyword={pageParam.keyword}
                                           highlightClassName="text-highlight"
                            />
                        </div>
                        <div role="cell"
                             className="selection-table-td px-[15px] flex items-center"
                        >
                            <button aria-label={`${bulkTest.file_name ?? defaultTestCaseFileName} ${intl.formatMessage({ id: "b0005" })}`}
                                    className="group flex items-center gap-2 text-black-400 font-[400]"
                                    onClick={() => onClickDownloadTestCase(bulkTest)}
                            >
                                {bulkTest.file_name ?? defaultTestCaseFileName}
                                <DownloadIcon aria-hidden="true"
                                              className="size-4 opacity-0 group-hover:opacity-100 group-focus:opacity-100"
                                />
                            </button>
                        </div>
                        <div role="cell"
                             className="selection-table-td px-[17px]"
                        >
                            {bulkTest.test_case_count}
                        </div>
                        <div role="cell"
                             className="selection-table-td px-[15px] flex items-center"
                        >
                            {bulkTest.status === "done" && (
                                <button aria-label={intl.formatMessage({ id: "b0005" })}
                                        className="group flex items-center gap-1"
                                        onClick={() => onClickDownloadTestResult(bulkTest)}
                                >
                                    <div className="badge-info">
                                        {intl.formatMessage({ id: "b0011" })}
                                    </div>
                                    <DownloadIcon aria-hidden="true"
                                                  className="size-4 text-black-400 opacity-0 group-hover:opacity-100 group-focus:opacity-100"
                                    />
                                </button>
                            )}
                            {bulkTest.status === "progress" && (<>
                                <LoadingIcon aria-hidden="true"
                                             className="animate-spin size-4"
                                />
                                {testingId === bulkTest.id && testingProgress !== undefined && (
                                    <div aria-live="polite"
                                         aria-atomic="true"
                                         className="ml-1 text-[14px] text-black-400"
                                    >
                                        {testingProgress.toFixed(0)}%
                                    </div>
                                )}
                            </>)}
                            {bulkTest.status === "cancel" && (
                                intl.formatMessage({ id: "b0002" })
                            )}
                            {bulkTest.status === "failed" && (
                                <Tooltip message={bulkTest.error_message ?? ""}>
                                    <div role="status"
                                         aria-label={intl.formatMessage({ id: "b0012" })}
                                         className="badge-danger"
                                    >
                                        {intl.formatMessage({ id: "b0012" })}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                        <div role="cell"
                             className="selection-table-td px-[15px] number-cell"
                        >
                            {UTCToLocalString(bulkTest.created_at, "YYYY-MM-DD HH:mm")}
                        </div>
                    </div>
                ))}
                {bulkTestPage.content.length === 0 && (
                    <div role="status"
                         className="empty-notice"
                         data-testid="empty-notice"
                    >
                        {intl.formatMessage({ id: pageParam.keyword ? "b0010" : "b0013" })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkTestTable;